import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ViewQuilt as LayoutIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as articleService from '../../services/articleService';

/**
 * چیدمان هیروی بلاگ — هر هفت جایگاهِ بالای صفحهٔ /blog را ادمین اینجا می‌چیند:
 * مطلب اصلی، سه ردیف بندانگشتی و سه ردیف فقط‌تیتر.
 *
 * هر جایگاه دقیقاً یک مقالهٔ منتشرشده دارد. جایگاهی که خالی بماند در سایت
 * خودکار پر می‌شود (تصویردارها از مطالب ویژه، تیتری‌ها از آخرین مطالب)، پس
 * برای کنترل کامل باید هر هفت جایگاه چیده شود.
 */

const { BLOG_HERO_POSITIONS, BLOG_HERO_POSITION_LABELS, BLOG_HERO_POSITION_HINTS } = articleService;
type HeroPosition = articleService.BlogHeroPosition;

const TEXT_POSITIONS: HeroPosition[] = ['hero-text-1', 'hero-text-2', 'hero-text-3'];

interface BlogHeroLayoutProps {
  /** بعد از هر تغییر جایگاه صدا زده می‌شود تا جدول مقالات هم تازه شود */
  onChanged?: () => void;
}

const BlogHeroLayout: React.FC<BlogHeroLayoutProps> = ({ onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [layout, setLayout] = useState<Record<string, articleService.Article | null>>({});
  const [loading, setLoading] = useState(true);
  const [savingSlot, setSavingSlot] = useState<HeroPosition | null>(null);

  // منبع انتخاب: مقالات منتشرشده (جستجوی سروری)
  const [options, setOptions] = useState<articleService.Article[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [search, setSearch] = useState('');

  const loadLayout = useCallback(async () => {
    setLoading(true);
    try {
      const data = await articleService.getBlogHeroLayout();
      setLayout(data || {});
    } catch {
      // خطا را روی کارت نشان نمی‌دهیم؛ جدول مقالات همچنان کار می‌کند
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  // جستجوی مقالات منتشرشده با دیبونس
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setOptionsLoading(true);
      try {
        const res = await articleService.getArticles(1, 30, {
          status: 'published',
          search: search.trim() || undefined,
        });
        if (!cancelled) setOptions(res?.docs || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const assignedIds = useMemo(
    () => new Set(Object.values(layout).filter(Boolean).map((a) => (a as articleService.Article)._id)),
    [layout]
  );

  const applyPosition = async (
    position: HeroPosition,
    article: articleService.Article | null,
    previous: articleService.Article | null
  ) => {
    setSavingSlot(position);
    try {
      if (article) {
        await articleService.setBlogHeroPosition(article._id, position);
        enqueueSnackbar(`«${article.title}» در جایگاه «${BLOG_HERO_POSITION_LABELS[position]}» نشست`, {
          variant: 'success',
        });
      } else if (previous) {
        await articleService.setBlogHeroPosition(previous._id, null);
        enqueueSnackbar(`جایگاه «${BLOG_HERO_POSITION_LABELS[position]}» خالی شد`, { variant: 'success' });
      }
      await loadLayout();
      onChanged?.();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در تنظیم جایگاه هیرو', { variant: 'error' });
    } finally {
      setSavingSlot(null);
    }
  };

  const filledCount = BLOG_HERO_POSITIONS.filter((p) => layout[p]).length;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <LayoutIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          چیدمان هیروی بلاگ
        </Typography>
        <Chip
          size="small"
          label={`${filledCount} از ${BLOG_HERO_POSITIONS.length} جایگاه چیده شده`}
          color={filledCount === BLOG_HERO_POSITIONS.length ? 'success' : 'default'}
          variant="outlined"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={loadLayout} disabled={loading}>
          بروزرسانی
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        بالای صفحهٔ /blog هفت جایگاه دارد: مطلب اصلی (تصویر بزرگ)، سه ردیف بندانگشتی و سه ردیف فقط‌تیتر.
        هر جایگاهی که اینجا خالی بگذارید سایت خودکار پر می‌کند — برای کنترل کامل هر هفت جایگاه را بچینید.
        فقط مقالات منتشرشده قابل انتخاب‌اند.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {BLOG_HERO_POSITIONS.map((position) => {
            const current = layout[position] || null;
            const isText = TEXT_POSITIONS.includes(position);
            const busy = savingSlot === position;

            return (
              <Box
                key={position}
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: current ? 'primary.light' : 'divider',
                  borderRadius: 2,
                  bgcolor: current ? 'action.hover' : 'transparent',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isText ? (
                    <TextIcon fontSize="small" color="action" />
                  ) : (
                    <ImageIcon fontSize="small" color="action" />
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {BLOG_HERO_POSITION_LABELS[position]}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {busy ? (
                    <CircularProgress size={18} />
                  ) : (
                    current && (
                      <Tooltip title="خالی کردن جایگاه">
                        <IconButton size="small" onClick={() => applyPosition(position, null, current)}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {BLOG_HERO_POSITION_HINTS[position]}
                </Typography>

                <Autocomplete<articleService.Article, false, false, false>
                  size="small"
                  disabled={busy}
                  value={current}
                  options={options}
                  loading={optionsLoading}
                  filterOptions={(x) => x}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  getOptionLabel={(option) => option.title || ''}
                  onInputChange={(_e, value, reason) => {
                    if (reason === 'input') setSearch(value);
                  }}
                  onChange={(_e, value) => applyPosition(position, value, current)}
                  noOptionsText="مقاله‌ای یافت نشد"
                  loadingText="در حال جستجو..."
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option._id} sx={{ gap: 1 }}>
                      <Avatar src={option.featuredImage} variant="rounded" sx={{ width: 40, height: 24 }}>
                        <ImageIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {option.title}
                      </Typography>
                      {assignedIds.has(option._id) && <Chip size="small" label="در هیرو" variant="outlined" />}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={current ? '' : 'انتخاب مقاله — خالی یعنی خودکار'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {optionsLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default BlogHeroLayout;
